export type MatrixStdAction = 'view' | 'add' | 'edit' | 'delete' | 'approve' | 'download'

const normalizeMatrixCode = (value: string): string => value.trim().toUpperCase()

export type MatrixActionPlacement =
  | {kind: 'standard'; action: MatrixStdAction}
  | {kind: 'extra'; label: string}
  | {kind: 'hidden-alias'}

/** Legacy codes hidden from the matrix; runtime still resolves via alias expansion. */
const HIDDEN_MATRIX_ALIASES = new Set<string>([
  'READ_PROCUREMENT_OVERVIEW',
  'PROCUREMENT_LOG_VIEW',
  'USER_PERMISSION_VIEW',
  'USER_PERMISSION_MANAGE',
  'REQUISITION_VIEW',
  'REQUISITION_CREATE',
  'REQUISITION_EDIT',
  'REQUISITION_DELETE',
  'PO_VIEW',
  'PO_CREATE',
  'PO_EDIT',
  'UPDATE_PO',
  'GRN_VIEW',
  'GRN_CREATE',
  'GRN_EDIT',
  'DELIVERY_VIEW',
  'DELIVERY_CREATE',
  'READ_INVOICE',
  'READ_INVOICE_MGMT',
  'READ_INVOICE_VIEW',
  'READ_INVOICE_EDIT',
  'READ_DELIVERY_TRAC',
  'READ_DELIVERY_TRACK',
  'READ_DELIVERY_UPDATED',
  'READ_DELIVERY_VIEW',
  'READ_LIBRARY_HEAD',
  'MANAGE_LIBRARY_HEAD',
  'READ_LIBRARY_SUBHEAD',
  'MANAGE_LIBRARY_SUBHEAD',
  'READ_LIBRARY_SUBCATEGORY',
  'MANAGE_LIBRARY_SUBCATEGORY',
  'READ_LIBRARY_ACCOUNTING',
  'MANAGE_LIBRARY_ACCOUNTING',
  'READ_LIBRARY_SUBACCOUNTING',
  'MANAGE_LIBRARY_SUBACCOUNTING',
  'READ_LIBRARY_ADDRESS',
  'MANAGE_LIBRARY_ADDRESS',
  'VIEW_VENDOR_LIBRARY',
  'MANAGE_VENDOR_LIBRARY',
])

/** When two codes compete for the same standard slot, prefer the canonical catalogue code. */
export const PROCUREMENT_STD_PREFERENCE: Partial<Record<MatrixStdAction, string[]>> = {
  view: [
    'READ_REQUISITION',
    'READ_PURCHASE_ORDER',
    'READ_DELIVERY_TRACKING',
    'READ_GOODS_RECEIPT',
    'INVOICE_VIEW',
    'RFQ_VIEW',
    'QUOTATION_VIEW',
  ],
  add: ['CREATE_REQUISITION', 'CREATE_PURCHASE_ORDER', 'CREATE_GOODS_RECEIPT', 'RFQ_CREATE', 'INVOICE_CREATE'],
  edit: ['UPDATE_REQUISITION', 'UPDATE_PURCHASE_ORDER', 'UPDATE_DELIVERY_TRACKING', 'RFQ_EDIT', 'INVOICE_EDIT'],
  delete: ['DELETE_REQUISITION'],
  download: ['REQUISITION_DOWNLOAD', 'INVOICE_DOWNLOAD'],
}

const EXPLICIT_EXTRA: Record<string, string> = {
  PO_DIRECT_CREATE: 'Add Direct PO',
  REQUISITION_ADD_ITEMS: 'Add Item',
  REQUISITION_SUBMIT: 'Submit',
  REQUISITION_APPROVE_VESSEL: 'Approve Vessel',
  REQUISITION_APPROVE_SHORE: 'Approve Shore',
  REQUISITION_EXPORT: 'Export',
  RFQ_SEND: 'Send',
  QUOTATION_COMPARE: 'Compare',
  QUOTATION_SELECT: 'Select',
  QUOTATION_REWORK: 'Rework',
  QUOTATION_SEND_TO_VESSEL: 'Send To Vessel',
  QUOTATION_SEND_TO_OWNER: 'Send To Owner',
  QUOTATION_OWNER_APPROVE: 'Owner Approve',
  QUOTATION_OWNER_REJECT: 'Owner Reject',
  PO_SEND: 'Send',
  PO_OWNER_APPROVE: 'Owner Approve',
  PO_OWNER_REJECT: 'Owner Reject',
  INVOICE_APPROVE: 'Approve',
  INVOICE_EXPORT: 'Export',
  USER_PERMISSION_MANAGE: 'Manage',
  LIBRARY_HEAD_MANAGE: 'Manage',
  LIBRARY_SUBHEAD_MANAGE: 'Manage',
  LIBRARY_SUBCATEGORY_MANAGE: 'Manage',
  LIBRARY_ACCOUNTING_MANAGE: 'Manage',
  LIBRARY_SUBACCOUNTING_MANAGE: 'Manage',
  LIBRARY_ADDRESS_MANAGE: 'Manage',
  VENDOR_LIBRARY_MANAGE: 'Manage',
}

export function isProcurementMenuLabel(menuLabel: string): boolean {
  return menuLabel.trim().toLowerCase() === 'procurement'
}

export function normalizeMatrixSubMenuLabel(subMenuLabel: string): string {
  const trimmed = (subMenuLabel || '').trim()
  return trimmed.length > 0 ? trimmed : '—'
}

export function procurementNavigationRowKey(
  moduleName: string,
  menuName: string,
  subMenuName: string
): string {
  return `${moduleName}::${menuName}::${normalizeMatrixSubMenuLabel(subMenuName)}`
}

export function resolveProcurementMatrixAction(permissionCode: string): MatrixActionPlacement | null {
  const code = normalizeMatrixCode(permissionCode)
  if (HIDDEN_MATRIX_ALIASES.has(code)) return {kind: 'hidden-alias'}

  const explicit = EXPLICIT_EXTRA[code]
  if (explicit) return {kind: 'extra', label: explicit}

  if (code.endsWith('_EXPORT')) return {kind: 'extra', label: 'Export'}

  if (code === 'CREATE_REQUISITION') return {kind: 'standard', action: 'add'}
  if (code === 'UPDATE_REQUISITION') return {kind: 'standard', action: 'edit'}
  if (code === 'DELETE_REQUISITION') return {kind: 'standard', action: 'delete'}
  if (code === 'READ_REQUISITION') return {kind: 'standard', action: 'view'}
  if (code === 'REQUISITION_DOWNLOAD') return {kind: 'standard', action: 'download'}

  if (code === 'READ_PURCHASE_ORDER') return {kind: 'standard', action: 'view'}
  if (code === 'CREATE_PURCHASE_ORDER') return {kind: 'standard', action: 'add'}
  if (code === 'UPDATE_PURCHASE_ORDER') return {kind: 'standard', action: 'edit'}

  if (code === 'READ_DELIVERY_TRACKING') return {kind: 'standard', action: 'view'}
  if (code === 'CREATE_DELIVERY_TRACKING') return {kind: 'standard', action: 'add'}
  if (code === 'UPDATE_DELIVERY_TRACKING') return {kind: 'standard', action: 'edit'}

  if (code === 'READ_GOODS_RECEIPT') return {kind: 'standard', action: 'view'}
  if (code === 'CREATE_GOODS_RECEIPT') return {kind: 'standard', action: 'add'}

  if (code === 'INVOICE_VIEW') return {kind: 'standard', action: 'view'}
  if (code === 'INVOICE_CREATE') return {kind: 'standard', action: 'add'}
  if (code === 'INVOICE_EDIT') return {kind: 'standard', action: 'edit'}
  if (code === 'INVOICE_DOWNLOAD') return {kind: 'standard', action: 'download'}

  if (code === 'RFQ_VIEW') return {kind: 'standard', action: 'view'}
  if (code === 'RFQ_CREATE') return {kind: 'standard', action: 'add'}
  if (code === 'RFQ_EDIT') return {kind: 'standard', action: 'edit'}

  if (code === 'QUOTATION_VIEW') return {kind: 'standard', action: 'view'}

  if (code === 'READ_LIBRARY_CATEGORY') return {kind: 'standard', action: 'view'}
  if (code === 'CREATE_LIBRARY_CATEGORY') return {kind: 'standard', action: 'add'}
  if (code === 'UPDATE_LIBRARY_CATEGORY') return {kind: 'standard', action: 'edit'}
  if (code === 'DELETE_LIBRARY_CATEGORY') return {kind: 'standard', action: 'delete'}
  if (code === 'LIBRARY_HEAD_VIEW') return {kind: 'standard', action: 'view'}
  if (code === 'LIBRARY_SUBHEAD_VIEW') return {kind: 'standard', action: 'view'}
  if (code === 'LIBRARY_SUBCATEGORY_VIEW') return {kind: 'standard', action: 'view'}
  if (code === 'LIBRARY_ACCOUNTING_VIEW') return {kind: 'standard', action: 'view'}
  if (code === 'LIBRARY_SUBACCOUNTING_VIEW') return {kind: 'standard', action: 'view'}
  if (code === 'LIBRARY_ADDRESS_VIEW') return {kind: 'standard', action: 'view'}
  if (code === 'VENDOR_LIBRARY_VIEW') return {kind: 'standard', action: 'view'}

  return null
}

export function stdPreferenceRank(action: MatrixStdAction, permissionCode: string): number {
  const prefs = PROCUREMENT_STD_PREFERENCE[action] || []
  const idx = prefs.indexOf(normalizeMatrixCode(permissionCode))
  return idx >= 0 ? idx : prefs.length + 1
}
